import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// Create axios instance
const apiClient = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // send cookies automatically
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

// Process queue for requests while refresh is happening
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Check if access token is expired
const isTokenExpired = (token) => {
    try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return Date.now() >= decoded.exp * 1000;
    } catch {
        return true; // treat any decode error as expired
    }
};

// Request interceptor
apiClient.interceptors.request.use(async (config) => {
    let token = localStorage.getItem('access');

    if (token && isTokenExpired(token)) {
        if (!isRefreshing) {
            isRefreshing = true;

            try {
                const response = await axios.post(
                    `${BASE_URL}/token/refresh/`,
                    {},
                    { withCredentials: true }
                );

                const newAccessToken = response.data.access;
                localStorage.setItem('access', newAccessToken);
                processQueue(null, newAccessToken);
                token = newAccessToken;
            } catch (err) {
                processQueue(err, null);
                localStorage.removeItem('access');
                throw err;
            } finally {
                isRefreshing = false;
            }
        } else {
            // Wait for ongoing refresh
            await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((newToken) => {
                token = newToken;
            });
        }
    }

    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
}, (error) => Promise.reject(error));

// Response interceptor
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (
            originalRequest.url.includes("/auth/login") 
        ) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;

                try {
                    const response = await axios.post(
                        `${BASE_URL}/token/refresh/`,
                        {},
                        { withCredentials: true }
                    );

                    const newAccessToken = response.data.access;
                    localStorage.setItem('access', newAccessToken);
                    processQueue(null, newAccessToken);

                    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return apiClient(originalRequest);
                } catch (err) {
                    processQueue(err, null);
                    localStorage.removeItem('access');
                    throw err;
                } finally {
                    isRefreshing = false;
                }
            } else {
                // Wait for ongoing refresh
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(apiClient(originalRequest));
                        },
                        reject,
                    });
                });
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;
